import {
  CLONE_META_DETAIL_TEXT_SX,
  TableMetaStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type TableSegmentBreakdownRow = {
  key: string;
  label: ReactNode;
  count: number;
  color?: string | null;
  icon?: ReactElement | null;
};

type TableSegmentBreakdownCellProps = {
  totalLabel: ReactNode;
  totalIcon?: ReactElement | null;
  rows: TableSegmentBreakdownRow[];
  truncateLabels?: boolean;
};

const buildLabelSx = (truncateLabels: boolean): SxProps<Theme> =>
  truncateLabels
    ? {
        display: 'block',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {
        display: 'block',
        minWidth: 0,
        overflow: 'visible',
        textOverflow: 'clip',
        whiteSpace: 'normal',
      };

const TOTAL_ICON_SX = {
  fontSize: (theme: Parameters<typeof resolveSemanticColor>[0]) => theme.app.iconSizes.sm,
  flexShrink: 0,
  color: (theme: Parameters<typeof resolveSemanticColor>[0]) =>
    resolveSemanticColor(theme, 'textSecondary'),
} as const;

const ROW_ICON_SX = {
  fontSize: (theme: Parameters<typeof resolveSemanticColor>[0]) => theme.app.iconSizes.sm,
  flexShrink: 0,
} as const;

const ROW_DOT_SX = {
  width: '8px',
  height: '8px',
  borderRadius: r('sm'),
  flexShrink: 0,
  ml: s('sm'),
  mr: s('sm'),
} as const;

export const TableSegmentBreakdownCell = ({
  totalLabel,
  totalIcon = null,
  rows,
  truncateLabels = true,
}: TableSegmentBreakdownCellProps): ReactElement => {
  const visibleRows = rows.filter((row) => row.count > 0);
  const hasTotalRow =
    totalLabel !== '' && totalLabel !== null && totalLabel !== undefined
      ? true
      : totalIcon !== null;

  return (
    <TableMetaStack rowGap={s('xs')}>
      {hasTotalRow ? (
        <Text
          role="captionSmall"
          color="textSecondary"
          dataGridTypography="custom"
          sx={CLONE_META_DETAIL_TEXT_SX}
        >
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: s('xs'),
              minWidth: 0,
            }}
          >
            {totalIcon ? (
              <Box component="span" sx={TOTAL_ICON_SX}>
                {totalIcon}
              </Box>
            ) : null}
            <Box component="span" sx={buildLabelSx(truncateLabels)}>
              {totalLabel}
            </Box>
          </Box>
        </Text>
      ) : null}
      {visibleRows.map((row) => (
        <Text
          key={row.key}
          role="captionSmall"
          color="textSecondary"
          dataGridTypography="custom"
          sx={CLONE_META_DETAIL_TEXT_SX}
        >
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: s('xs'),
              minWidth: 0,
            }}
          >
            {row.icon ? (
              <Box component="span" sx={ROW_ICON_SX}>
                {row.icon}
              </Box>
            ) : row.color ? (
              <Box component="span" sx={[ROW_DOT_SX, { backgroundColor: row.color }]} />
            ) : null}
            <Box component="span" sx={buildLabelSx(truncateLabels)}>
              {row.label}
            </Box>
          </Box>
        </Text>
      ))}
    </TableMetaStack>
  );
};
